import '@/theme/css/app.css';

import { version } from '../../package.json';
import qs from 'qs';

// polyfills
import '@/polyfills/array.toReversed';

import { type Dictionary, omitBy, isNil } from 'lodash';
import config from '@/config';
import bridge from '@thermenresorts/bridge';
import { createApp } from 'vue';
import { createI18n } from 'vue-i18n';
import { createPinia } from 'pinia';
import { stores } from '@/stores';
import { messages, type Locales, type MessageSchema } from '@/translations';
import router from '@/router';
import App from '@/views/App.vue';
import { init as initSentry, replayIntegration, thirdPartyErrorFilterIntegration } from '@sentry/vue';

console.log(`Thermen Reservation Flow (v${version})`);

const pinia = createPinia();
const app = createApp(App);

export const i18n = createI18n<[MessageSchema], Locales>({
	legacy: false,
	locale: config.locale,
	warnHtmlMessage: false,
	fallbackLocale: 'en-US',
	messages,
});

if (!import.meta.env.DEV) {
	initSentry({
		app,
		dsn: 'https://8f4a919227a01837ea0487e892e91ca2@o398254.ingest.sentry.io/4506579637960704',
		environment: import.meta.env.MODE,
		integrations: [
			replayIntegration({
				maskAllText: false,
				blockAllMedia: false,
			}),
			thirdPartyErrorFilterIntegration({
				filterKeys: ['thermen-reservation'],
				behaviour: 'drop-error-if-contains-third-party-frames',
			}),
		],
		tracesSampleRate: 1.0,
		replaysSessionSampleRate: 0.1,
		replaysOnErrorSampleRate: 1.0,
		ignoreErrors: [
			'CanceledError',
			'IDBFactory.open()', // reservation-flow#118: Disable these because it's because of injected third-party software.
			'Rage Click', // reservation-flow#118: No need to explain this.. right?
			'AxiosError',
			'WKWebView API', // #3334: Only happening on DuckDuckGo browsers because of it being blocked
			'(evaluating ', // Common pattern for node_modules/ packages
		],
		denyUrls: [
			/rum-static\.pingdom\.net\/.*\.js$/,
			/consent\.cookiebot\.com\/.*\.js$/,
			/googletagmanager\.com\/.*\.js$/,
			/static\.zdassets\.com\/.*\.js$/,
			/edge\.marker\.io\/.*\.js$/,
			/cdn-4\.convertexperiments\.com\/.*\.js$/,
			/cdn\.seamly-app\.com\/.*\.js$/,
		],
	});
}

app.use(i18n);
app.use(pinia);
app.use(stores);
app.use(router);

const container = document.getElementById('app');
if (container) {
	document.body.classList.add(`theme-${config.theme}`);
	app.mount(container);

	bridge.receiveMessages('reservation-flow', {
		'reservation-flow.open': (data) => {
			const hasPayload = Object.values(data ?? {}).some((value) => {
				if (Array.isArray(value)) return value.length > 0;
				return value !== undefined && value !== null && value !== '';
			});

			if (!hasPayload) {
				return;
			}

			const currentQuery = qs.parse(window.location.search, { ignoreQueryPrefix: true }) as Dictionary<string>;

			const x = omitBy(
				{
					checkout: data.checkout ? data.checkout.toString() : null,
					product: data.product ? data.product.toString() : currentQuery.product,
					voucher_code: data.voucher_code, // @deprecated since 8-4-2024
					voucher_codes: data.voucher_codes,
					arrival_date: data.arrival_date,
					departure_date: data.departure_date,
					bath_card_id: data.bath_card_id ? data.bath_card_id.toString() : undefined,
					persons_count: data.persons_count ? data.persons_count.toString() : undefined,
					persons: data.persons,
					access_token: data.access_token,
					checkout_type: data.checkout_type,
					nights: data.nights,
					lang: data.lang,
					ro: data.ro ?? currentQuery.ro,
					ga_origin: data.ga_origin,
					_gl: data._gl,
				},
				isNil,
			) as Dictionary<string>;

			console.log('Thermen Reservation flow received: reservation-flow.open', x);

			const nextSearch = qs.stringify(x, { addQueryPrefix: true });

			if (nextSearch === window.location.search) {
				return;
			}

			window.location.search = nextSearch;
		},
	});

	bridge.sendMessage('reservation-flow', 'reservation-flow.ready');
}

export {};
